import * as yup from 'yup';
import moment from 'moment';
import {
  AccountDTO,
  GrantDTO,
  GranTypeDTO,
} from '../../../../Profile/Accounts/api/useAccountsQuery';
import { DATE_FORMAT } from '../../../../CommonComponents/OPL/dates/utils';
import { OptionsPerGrantTypeDTO } from '../../api/useStockOptionsQuery';
import { OwnerModelDTO } from '../../StockOptions.meta';

interface FormContext {
  OptionsPerGrantType: OptionsPerGrantTypeDTO[];
}

interface VestingListItem {
  VestedShares: number | null;
  VestingDate: string | null;
  PricePerShare: number | null;
  ExpirationDate: string | null;
  GrantDate: string | null;
  DispositionStrategy: number | null;
  DispositionYear: string | null;
  DispositionDate: string | null;
  ExerciseStrategy: number | null;
  ExerciseYear: string | null;
  ExerciseDate: string | null;
}

export interface Grant extends Omit<GrantDTO, 'Id' | 'VestingList'> {
  Id?: string;
  VestingList: Partial<VestingListItem>[];
}

export interface Account extends Partial<Omit<AccountDTO, 'Id' | 'GrantList'>> {
  Id?: string;
  GrantList: Grant[];
}

export interface FormValues {
  name: string;
  owner: string;
  grantType: GranTypeDTO;
  marketValue: number;
  totalShares: number;
  customAssetBucketIndex: number | null;
  grantDate: string | null;
  price: number;
  expectedReturn: number | null;
  assetClass: number;
  earmarks: { label: string; value: string }[];
  vestingList: Partial<VestingListItem>[];
  ownedShares: number | null;
  ownedExerciseDate: string | null;
  ownedStrikePrice: number | null;
  ownedFmvAtExercise: number | null;
  ownedDispositionStrategy: number | null;
  ownedDispositionDate: string | null;
  ownedDispositionYear: string | null;
  orionManagementStyleId: number | null;
  ownerModel: OwnerModelDTO[];
  product: { id: number; ticker: string; price: number } | null;
}

export const getDispositionExerciseOptions = (
  grantType: number,
  OptionsPerGrantType: OptionsPerGrantTypeDTO[],
) => {
  const selectedGrantOption = OptionsPerGrantType.find(item => item.GrantType === grantType);
  if (selectedGrantOption) {
    return [
      selectedGrantOption?.AvailableDispositionOptions,
      selectedGrantOption?.AvailableExerciseOptions,
    ];
  }
  return [null, null];
};

// Exercises and dispositions are projected monthly, so the current month is already under way and
// cannot be scheduled.
export const getEarliestSchedulableDate = () =>
  moment()
    .add(1, 'month')
    .startOf('month');

// Neither an exercise nor a disposition can precede the vesting it draws from.
export const getEarliestDateAfterVesting = (vestingDate?: string | null) => {
  const earliest = getEarliestSchedulableDate();
  return vestingDate ? moment.max(earliest, moment(vestingDate, DATE_FORMAT)) : earliest;
};

interface FieldError {
  path: string;
  message?: string;
}

export const validation = yup.object().shape({
  grantType: yup
    .number()
    .required('Please select an Asset Type.'),
  name: yup
    .string()
    .nullable()
    .required('Please specify a Company Name.'),
  owner: yup
    .string()
    .nullable()
    .required('Please specify an owner for this account.'),
  totalShares: yup
    .number()
    .nullable()
    .test('total-shares-greater-than-zero', 'Total Shares must be greater than 0.', value => {
      if (typeof value !== 'number') {
        return false;
      }
      return value > 0;
    }),
  vestingList: yup
    .array()
    .of(
      yup.object().shape({
        VestingDate: yup.string().nullable(),
        VestedShares: yup.number().nullable(),
        DispositionStrategy: yup.number().nullable(),
        DispositionDate: yup.string().nullable(),
        DispositionYear: yup.string().nullable(),
        ExerciseStrategy: yup.number().nullable(),
        ExerciseDate: yup.string().nullable(),
        ExerciseYear: yup.string().nullable(),
      }),
    )
    .test('mandatory-fields-for-options', 'Please fill required Fields', function(value, ctx) {
      const totalShares = ctx.parent.totalShares ?? 0;
      const ownedShares = ctx.parent.ownedShares ?? 0;
      const totalScheduledShares = value?.reduce(
        (acc, vest) => acc + (vest.VestedShares ?? 0),
        0,
      ) ?? 0;

      const errors: FieldError[] = [];

      if (totalScheduledShares + ownedShares > totalShares && totalShares > 0) {
        errors.push({
          path: 'vestingList',
          message: `Total allocated shares (${(totalScheduledShares + ownedShares).toLocaleString()} scheduled + owned) cannot exceed total granted shares (${totalShares.toLocaleString()}).`,
        });
      }

      if (
        value &&
        value.every(
          obj => obj && Object.values(obj).every(v => v === null || v === undefined || v === ''),
        )
      ) {
        if (errors.length > 0) {
          return new yup.ValidationError(
            errors.map(
              e =>
                new yup.ValidationError(
                  e.message ?? 'Please fill all the required fields',
                  null,
                  e.path,
                ),
            ),
            null,
            this.path,
          );
        }
        return true;
      }

      const grantType = ctx.parent.grantType;
      const { OptionsPerGrantType } = ctx.options.context as FormContext;
      const [dispositionOptions, exerciseOptions] = getDispositionExerciseOptions(
        grantType,
        OptionsPerGrantType,
      );

      value?.map((vest, index) => {
        if (!vest.VestingDate) {
          errors.push({ path: `vestingList[${index}].VestingDate` });
        }
        if (!vest.VestedShares) {
          errors.push({ path: `vestingList[${index}].VestedShares` });
        }
        if (dispositionOptions && vest.DispositionStrategy === null) {
          errors.push({ path: `vestingList[${index}].DispositionStrategy` });
        }
        if (exerciseOptions && vest.ExerciseStrategy === null) {
          errors.push({ path: `vestingList[${index}].ExerciseStrategy` });
        }
        const selectedDisposition = dispositionOptions
          ? dispositionOptions.find(opt => opt.Value === vest.DispositionStrategy)
          : null;
        const selectedExercise = exerciseOptions
          ? exerciseOptions.find(opt => opt.Value === vest.ExerciseStrategy)
          : null;
        if (selectedDisposition) {
          if (selectedDisposition.ShowDateSelector) {
            if (vest.DispositionDate === null) {
              errors.push({ path: `vestingList[${index}].DispositionDate` });
            } else if (
              vest.VestingDate &&
              moment(vest.DispositionDate).isBefore(vest.VestingDate)
            ) {
              errors.push({
                path: `vestingList[${index}].DispositionDate`,
                message: 'Disposition Date should be after Vesting Date',
              });
            }
          } else if (selectedDisposition.ShowYearSelector) {
            if (!(vest.DispositionYear && parseInt(vest.DispositionYear))) {
              errors.push({ path: `vestingList[${index}].DispositionYear` });
            } else if (
              vest.VestingDate &&
              moment()
                .add(vest.DispositionYear ? parseInt(vest.DispositionYear) : 0, 'years')
                .isBefore(vest.VestingDate)
            ) {
              errors.push({
                path: `vestingList[${index}].DispositionYear`,
                message: 'Disposition Year should be after Vesting Date',
              });
            }
          }
        }
        if (selectedExercise) {
          if (selectedExercise.ShowDateSelector) {
            if (vest.ExerciseDate === null) {
              errors.push({ path: `vestingList[${index}].ExerciseDate` });
            } else if (moment(vest.ExerciseDate).isBefore(getEarliestSchedulableDate(), 'day')) {
              errors.push({
                path: `vestingList[${index}].ExerciseDate`,
                message: `Exercise Date must be on or after ${getEarliestSchedulableDate().format(
                  DATE_FORMAT,
                )}.`,
              });
            } else if (vest.VestingDate && moment(vest.ExerciseDate).isBefore(vest.VestingDate)) {
              errors.push({
                path: `vestingList[${index}].ExerciseDate`,
                message: 'Exercise Date should be after Vesting Date',
              });
            }
          } else if (selectedExercise.ShowYearSelector) {
            if (!(vest.ExerciseYear && parseInt(vest.ExerciseYear))) {
              errors.push({ path: `vestingList[${index}].ExerciseYear` });
            } else if (
              vest.VestingDate &&
              moment()
                .add(vest.ExerciseYear ? parseInt(vest.ExerciseYear) : 0, 'years')
                .isBefore(vest.VestingDate)
            ) {
              errors.push({
                path: `vestingList[${index}].ExerciseYear`,
                message: 'Exercise Year should be after Vesting Date',
              });
            }
          }
        }
      });
      if (errors.length > 0) {
        return new yup.ValidationError(
          errors.map(
            e =>
              new yup.ValidationError(
                e.message ?? 'Please fill all the required fields',
                null,
                e.path,
              ),
          ),
          null,
          this.path,
        );
      }
      return true;
    }),
  ownedShares: yup
    .number()
    .nullable()
    .test(
      'owned-shares-not-exceed-total',
      'Owned shares cannot exceed total granted shares.',
      function(value) {
        if (typeof value !== 'number' || value <= 0) return true;
        const totalShares = this.parent.totalShares ?? 0;
        return value <= totalShares;
      },
    ),
  ownedExerciseDate: yup
    .string()
    .nullable()
    .when('ownedShares', {
      is: (val: number | null) => typeof val === 'number' && val > 0,
      then: schema =>
        schema.required('Please specify the date shares were exercised.'),
    }),
  ownedStrikePrice: yup
    .number()
    .nullable()
    .when(['ownedShares', 'grantType'], {
      is: (ownedShares: number | null, grantType: GranTypeDTO) =>
        typeof ownedShares === 'number' &&
        ownedShares > 0 &&
        (grantType === GranTypeDTO.Iso || grantType === GranTypeDTO.Nso),
      then: schema =>
        schema.required('Please specify the strike price at which shares were exercised.'),
    }),
  ownedFmvAtExercise: yup
    .number()
    .nullable()
    .when(['ownedShares', 'grantType'], {
      is: (ownedShares: number | null, grantType: GranTypeDTO) =>
        typeof ownedShares === 'number' &&
        ownedShares > 0 &&
        (grantType === GranTypeDTO.Iso || grantType === GranTypeDTO.Nso),
      then: schema =>
        schema.required('Please specify the fair market value per share at the time of exercise.'),
    }),
  ownedDispositionStrategy: yup.number().nullable(),
  ownedDispositionDate: yup.string().nullable(),
  ownedDispositionYear: yup.string().nullable(),
});
